import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ListTree, CalendarDays, Ruler, Users, FolderCog, History, Undo2, Plus, ShieldAlert } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtMoney, fmtNum, fmtDate } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useFormDraft } from "@/lib/drafts";
import { DraftPrompt, DraftRestoredBanner } from "@/components/DraftPrompt";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  ["wbs", "WBS", ListTree],
  ["schedule", "Schedule & dates", CalendarDays],
  ["quantities", "Quantities & BOQ", Ruler],
  ["users", "User accounts", Users],
  ["projects", "Project settings", FolderCog],
  ["audit", "Audit log", History],
];

const FIELD_SETS = {
  wbs: [
    ["name", "Activity name", "text"],
    ["code", "Code", "text"],
    ["description", "Description", "text"],
    ["level", "Level (1-3)", "number"],
    ["parent_id", "Parent activity id", "text"],
    ["sequence", "Sequence", "number"],
    ["department", "Responsible department", "text"],
    ["active", "Active", "bool"],
  ],
  schedule: [
    ["planned_start", "Planned start", "date"],
    ["planned_finish", "Planned end", "date"],
    ["actual_start", "Actual start", "date"],
    ["revised_finish", "Revised completion", "date"],
  ],
  quantities: [
    ["total_qty", "Planned quantity", "number"],
    ["unit", "Unit of measurement", "text"],
    ["rate", "Unit rate (INR)", "number"],
  ],
  users: [
    ["name", "Full name", "text"],
    ["company", "Company", "text"],
    ["designation", "Designation", "text"],
    ["email", "Email", "text"],
    ["role", "Role", "role"],
    ["phone", "Mobile", "text"],
    ["trial_started_at", "Trial started at (ISO)", "text"],
    ["locked", "Locked (deactivated)", "bool"],
    ["email_verified", "Email verified", "bool"],
  ],
  projects: [
    ["name", "Project name", "text"],
    ["client", "Client name", "text"],
    ["contract_value", "Contract value (INR)", "number"],
    ["location", "Location", "text"],
    ["industry", "Project type", "industry"],
    ["status", "Status", "status"],
    ["start_date", "Start date", "date"],
    ["finish_date", "Finish date", "date"],
  ],
};

const asType = (kind, raw) => (kind === "number" ? Number(raw) : kind === "bool" ? raw === "true" || raw === true : raw);

export default function AdminDataEditor() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("wbs");
  const [wbs, setWbs] = useState(null);
  const [users, setUsers] = useState(null);
  const [projects, setProjects] = useState(null);
  const [audit, setAudit] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const draft = useFormDraft({
    formId: "admin-editor",
    form: { editing },
    setForm: (next) => setEditing(next.editing),
    blank: { editing: null },
  });

  const loadAll = () => {
    api.get("/admin/editor/wbs").then(({ data }) => setWbs(data)).catch(() => {});
    api.get("/admin/editor/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/editor/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/admin/editor/audit").then(({ data }) => setAudit(data)).catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);

  if (user && user.role !== "superadmin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="editor-forbidden">
        <ShieldAlert className="w-10 h-10 text-[#F97316]" />
        <h1 className="text-base md:text-lg font-semibold">Super Admin access only</h1>
        <p className="text-sm text-muted-foreground max-w-[420px]">
          The data editor can change scope, quantities and user accounts across every project, so it is limited to the
          Super Admin role.
        </p>
        <Button size="sm" onClick={() => navigate("/projects")} data-testid="editor-back-btn">Back to portfolio</Button>
      </div>
    );
  }

  const openEdit = (module, record, id) => {
    const fields = FIELD_SETS[module];
    setEditing({
      module,
      id,
      record,
      values: Object.fromEntries(fields.map(([f]) => [f, record[f] ?? ""])),
      reason: "",
    });
  };

  const save = async () => {
    const fields = FIELD_SETS[editing.module];
    const changes = {};
    for (const [f, , kind] of fields) {
      const next = asType(kind, editing.values[f]);
      const current = editing.record[f] ?? (kind === "number" ? 0 : kind === "bool" ? false : "");
      if (String(next) !== String(current)) changes[f] = next;
    }
    if (!Object.keys(changes).length) { toast.error("Nothing changed"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/editor/${editing.module}/${encodeURIComponent(editing.id)}`, { changes, reason: editing.reason });
      toast.success(`Saved — ${Object.keys(changes).length} field(s) updated and logged`);
      setEditing(null);
      draft.clear();
      loadAll();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const revert = async (batchId) => {
    try {
      await api.post(`/admin/editor/audit/${batchId}/revert`);
      toast.success("Edit reverted — the original values are restored");
      loadAll();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not revert");
    }
  };

  const acts = wbs?.items || [];
  const stats = {
    activities: acts.length,
    edits: audit?.items?.length || 0,
    users: users?.items?.length || 0,
    projects: projects?.items?.length || 0,
  };

  return (
    <div className="min-h-screen" data-testid="admin-data-editor-page">
      <header className="h-14 border-b border-border flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-20 bg-background/85 backdrop-blur-md">
        <Logo size="sm" />
        <Badge2 tone="warn">Super Admin</Badge2>
        <button onClick={() => navigate("/projects")} className="ml-auto text-[13px] text-muted-foreground hover:text-foreground" data-testid="editor-portfolio-btn">
          Back to portfolio
        </button>
      </header>

      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        <PageHead
          title="Data editor"
          subtitle="Direct edit of scope, dates, quantities, accounts and project settings. Every change needs a reason, is logged, and can be reverted within 24 hours."
        />

        <DraftPrompt draft={draft} label="edit" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <Kpi label="Activities" value={stats.activities} testId="kpi-editor-activities" />
          <Kpi label="Projects" value={stats.projects} testId="kpi-editor-projects" />
          <Kpi label="User accounts" value={stats.users} testId="kpi-editor-users" />
          <Kpi label="Logged edits" value={stats.edits} tone="primary" testId="kpi-editor-edits" />
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {TABS.map(([key, text, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 border px-3 py-1.5 text-[12.5px] whitespace-nowrap transition-colors duration-150 ${
                tab === key ? "border-[#F97316] text-[#F97316] bg-[#F97316]/10" : "border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`editor-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {text}
            </button>
          ))}
        </div>

        {tab === "wbs" || tab === "schedule" || tab === "quantities" ? (
          <Panel
            title={tab === "wbs" ? "Work breakdown — all projects" : tab === "schedule" ? "Schedule & dates" : "Quantities & BOQ"}
            testId={`${tab}-panel`}
            action={tab === "wbs" ? (
              <Button size="sm" onClick={() => setCreating(true)} data-testid="editor-new-activity-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New activity
              </Button>
            ) : null}
          >
            <div className="overflow-x-auto">
              <table className="w-full dense-table">
                <thead className="sticky-head">
                  {tab === "quantities" ? (
                    <tr><th>Project</th><th>Code</th><th>Activity</th><th className="text-right">Planned qty</th><th>Unit</th><th className="text-right">Rate</th><th className="text-right">Total value</th><th>Variance</th><th /></tr>
                  ) : tab === "schedule" ? (
                    <tr><th>Project</th><th>Code</th><th>Activity</th><th>Planned start</th><th>Planned end</th><th>Actual start</th><th>Revised completion</th><th /></tr>
                  ) : (
                    <tr><th>Project</th><th>Code</th><th>Activity</th><th>Level</th><th>Department</th><th>Seq</th><th>Status</th><th /></tr>
                  )}
                </thead>
                <tbody>
                  {acts.map((a) => {
                    const origQty = a.original_total_qty;
                    const origRate = a.original_rate;
                    const changed = origQty !== undefined || origRate !== undefined;
                    return (
                      <tr key={a.id} data-testid={`editor-activity-${a.id}`}>
                        <td className="max-w-[150px] truncate text-muted-foreground">{a.project_name}</td>
                        <td className="num">{a.code}</td>
                        <td className="max-w-[240px] truncate font-medium">{a.name}</td>
                        {tab === "quantities" ? (
                          <>
                            <td className={`num text-right ${origQty !== undefined ? "text-amber-500 font-medium" : ""}`}>{fmtNum(a.total_qty, 2)}</td>
                            <td>{a.unit || "—"}</td>
                            <td className={`num text-right ${origRate !== undefined ? "text-amber-500 font-medium" : ""}`}>{fmtNum(a.rate, 2)}</td>
                            <td className="num text-right">{fmtMoney(a.value)}</td>
                            <td className="text-[11px]" data-testid={`variance-${a.id}`}>
                              {changed ? (
                                <span className="text-amber-500">
                                  was {origQty !== undefined ? `${fmtNum(origQty, 2)} ${a.unit}` : ""}
                                  {origRate !== undefined ? ` @ ${fmtNum(origRate, 2)}` : ""}
                                </span>
                              ) : <span className="text-muted-foreground">original</span>}
                            </td>
                          </>
                        ) : tab === "schedule" ? (
                          <>
                            <td className="num">{a.planned_start ? fmtDate(a.planned_start) : "—"}</td>
                            <td className="num">{a.planned_finish ? fmtDate(a.planned_finish) : "—"}</td>
                            <td className="num">{a.actual_start ? fmtDate(a.actual_start) : "—"}</td>
                            <td className={`num ${a.revised_finish ? "text-amber-500" : ""}`}>{a.revised_finish ? fmtDate(a.revised_finish) : "—"}</td>
                          </>
                        ) : (
                          <>
                            <td className="num">L{a.level}</td>
                            <td>{a.department || a.discipline || "—"}</td>
                            <td className="num">{a.sequence ?? "—"}</td>
                            <td><Badge2 tone={a.active === false ? "bad" : "good"}>{a.active === false ? "inactive" : "active"}</Badge2></td>
                          </>
                        )}
                        <td>
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openEdit(tab, a, a.id)} data-testid={`edit-${tab}-${a.id}`}>Edit</Button>
                        </td>
                      </tr>
                    );
                  })}
                  {wbs && !acts.length ? <tr><td colSpan={9} className="text-muted-foreground">No activities.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </Panel>
        ) : null}

        {tab === "users" ? (
          <Panel title="User accounts" testId="users-panel">
            <div className="overflow-x-auto">
              <table className="w-full dense-table">
                <thead className="sticky-head">
                  <tr><th>Email</th><th>Name</th><th>Company</th><th>Designation</th><th>Role</th><th>Plan</th><th>Trial start</th><th>State</th><th /></tr>
                </thead>
                <tbody>
                  {(users?.items || []).map((u) => (
                    <tr key={u.email} data-testid={`editor-user-${u.email}`}>
                      <td className="num">{u.email}</td>
                      <td>{u.name || "—"}</td>
                      <td>{u.company || "—"}</td>
                      <td>{u.designation || "—"}</td>
                      <td><Badge2 tone={u.role === "superadmin" ? "warn" : "primary"}>{u.role}</Badge2></td>
                      <td>{u.plan || "trial"}</td>
                      <td className="num text-[11px]">{u.trial_started_at ? String(u.trial_started_at).slice(0, 10) : "—"}</td>
                      <td>
                        <Badge2 tone={u.locked ? "bad" : u.email_verified ? "good" : "warn"}>
                          {u.locked ? "locked" : u.email_verified ? "active" : "unverified"}
                        </Badge2>
                      </td>
                      <td>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openEdit("users", u, u.email)} data-testid={`edit-users-${u.email}`}>Edit</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        ) : null}

        {tab === "projects" ? (
          <Panel title="Project settings" testId="projects-panel">
            <div className="overflow-x-auto">
              <table className="w-full dense-table">
                <thead className="sticky-head">
                  <tr><th>Project</th><th>Client</th><th>Location</th><th>Type</th><th className="text-right">Contract value</th><th className="text-right">Activities</th><th className="text-right">DPRs</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {(projects?.items || []).map((p) => (
                    <tr key={p.id} data-testid={`editor-project-${p.id}`}>
                      <td className="font-medium max-w-[240px] truncate">{p.name}</td>
                      <td>{p.client || "—"}</td>
                      <td>{p.location || "—"}</td>
                      <td className="uppercase text-[11px]">{p.industry}</td>
                      <td className="num text-right">{fmtMoney(p.contract_value)}</td>
                      <td className="num text-right">{p.activity_count}</td>
                      <td className="num text-right">{p.dpr_count}</td>
                      <td><Badge2 tone={p.status === "Active" ? "good" : p.status === "On Hold" ? "warn" : "primary"}>{p.status}</Badge2></td>
                      <td>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openEdit("projects", p, p.id)} data-testid={`edit-projects-${p.id}`}>Edit</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        ) : null}

        {tab === "audit" ? (
          <Panel title="Admin audit log — read only" testId="audit-panel">
            <p className="text-[11.5px] text-muted-foreground mb-3">
              Every field change is recorded permanently. Reverts are themselves logged. Edits can be undone for
              {" "}{audit?.revert_window_hours ?? 24} hours.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full dense-table">
                <thead className="sticky-head">
                  <tr><th>When</th><th>Admin</th><th>Module</th><th>Record</th><th>Field</th><th>Old value</th><th>New value</th><th>Reason</th><th /></tr>
                </thead>
                <tbody>
                  {(audit?.items || []).map((a) => (
                    <tr key={a.id} data-testid={`audit-row-${a.id}`}>
                      <td className="num text-[11px] whitespace-nowrap">{String(a.created_at).slice(0, 16).replace("T", " ")}</td>
                      <td className="num text-[11px]">{a.admin_email}</td>
                      <td className="uppercase text-[11px]">{a.module}</td>
                      <td className="max-w-[180px] truncate">{a.record_label}</td>
                      <td className="num text-[11px]">{a.field}</td>
                      <td className="max-w-[140px] truncate text-muted-foreground">{String(a.old_value ?? "—")}</td>
                      <td className="max-w-[140px] truncate text-amber-500">{String(a.new_value ?? "—")}</td>
                      <td className="max-w-[220px] truncate text-[11.5px]">{a.reason}</td>
                      <td>
                        {a.reverted ? <Badge2 tone="bad">reverted</Badge2> : a.revertable ? (
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => revert(a.batch_id)} data-testid={`revert-${a.batch_id}`}>
                            <Undo2 className="w-3 h-3 mr-1" /> Revert
                          </Button>
                        ) : <span className="text-[11px] text-muted-foreground">window closed</span>}
                      </td>
                    </tr>
                  ))}
                  {audit && !audit.items.length ? <tr><td colSpan={9} className="text-muted-foreground">No edits recorded yet.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </Panel>
        ) : null}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-[620px] max-h-[88vh] overflow-y-auto" data-testid="editor-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editing?.module} record</DialogTitle>
            <DialogDescription>
              {editing?.record?.name || editing?.record?.email} — changes are logged against your account and take
              effect for other users on their next page load.
            </DialogDescription>
          </DialogHeader>

          <DraftRestoredBanner draft={draft} />

          <div className="grid sm:grid-cols-2 gap-3.5">
            {(FIELD_SETS[editing?.module] || []).map(([field, label, kind]) => (
              <div key={field} className="space-y-1.5">
                <Label className="text-[12px]">{label}</Label>
                {kind === "bool" || kind === "role" || kind === "industry" || kind === "status" ? (
                  <Select
                    value={String(editing.values[field] ?? "")}
                    onValueChange={(v) => setEditing({ ...editing, values: { ...editing.values, [field]: v } })}
                  >
                    <SelectTrigger data-testid={`field-${field}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {(kind === "bool" ? ["true", "false"]
                        : kind === "role" ? (users?.roles || [])
                        : kind === "industry" ? (projects?.industries || [])
                        : (projects?.statuses || [])).map((opt) => (
                          <SelectItem key={opt} value={opt} data-testid={`option-${field}-${opt}`}>{opt}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={kind === "number" ? "number" : kind === "date" ? "date" : "text"}
                    value={editing.values[field] ?? ""}
                    onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [field]: e.target.value } })}
                    data-testid={`field-${field}`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="space-y-1.5 mt-2">
            <Label className="text-[12px]">Reason for edit <span className="text-[#F97316]">(required)</span></Label>
            <Textarea
              rows={2}
              value={editing?.reason || ""}
              onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
              placeholder="Client approved revised quantity vide letter NPCIL/2026/114 dated 12-06-2026."
              data-testid="editor-reason-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="editor-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={busy || (editing?.reason || "").trim().length < 5} data-testid="editor-save-btn">
              {busy ? "Saving…" : "Confirm & save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NewActivityDialog open={creating} onClose={() => setCreating(false)} projects={wbs?.projects || []} onDone={loadAll} />
    </div>
  );
}

const NEW_BLANK = { project_id: "", code: "", name: "", level: 3, unit: "Nos", total_qty: 0, rate: 0, department: "", reason: "" };

const NewActivityDialog = ({ open, onClose, projects, onDone }) => {
  const [form, setForm] = useState(NEW_BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/editor/wbs", {
        ...form, level: Number(form.level), total_qty: Number(form.total_qty), rate: Number(form.rate),
      });
      toast.success("Activity created and logged");
      setForm(NEW_BLANK);
      onClose();
      onDone();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[560px]" data-testid="new-activity-dialog">
        <DialogHeader>
          <DialogTitle>New WBS activity</DialogTitle>
          <DialogDescription>Adds an activity to an existing project. The creation is written to the audit log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div className="space-y-1.5">
            <Label className="text-[12px]">Project</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="new-activity-project"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`new-activity-project-${p.id}`}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-3.5">
            <div className="space-y-1.5"><Label className="text-[12px]">Code</Label><Input value={form.code} onChange={set("code")} data-testid="new-activity-code" /></div>
            <div className="space-y-1.5"><Label className="text-[12px]">Level</Label><Input type="number" value={form.level} onChange={set("level")} data-testid="new-activity-level" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-[12px]">Activity name</Label><Input value={form.name} onChange={set("name")} data-testid="new-activity-name" /></div>
          <div className="grid sm:grid-cols-3 gap-3.5">
            <div className="space-y-1.5"><Label className="text-[12px]">Unit</Label><Input value={form.unit} onChange={set("unit")} data-testid="new-activity-unit" /></div>
            <div className="space-y-1.5"><Label className="text-[12px]">Quantity</Label><Input type="number" value={form.total_qty} onChange={set("total_qty")} data-testid="new-activity-qty" /></div>
            <div className="space-y-1.5"><Label className="text-[12px]">Rate</Label><Input type="number" value={form.rate} onChange={set("rate")} data-testid="new-activity-rate" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-[12px]">Responsible department</Label><Input value={form.department} onChange={set("department")} data-testid="new-activity-dept" /></div>
          <div className="space-y-1.5">
            <Label className="text-[12px]">Reason for adding <span className="text-[#F97316]">(required)</span></Label>
            <Textarea rows={2} value={form.reason} onChange={set("reason")} data-testid="new-activity-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="new-activity-cancel">Cancel</Button>
          <Button onClick={create} disabled={busy || !form.project_id || !form.code || !form.name || form.reason.trim().length < 5} data-testid="new-activity-submit">
            {busy ? "Creating…" : "Create activity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
